import React from 'react';
import { LayoutGrid, Ruler, HomeIcon, DoorOpen } from 'lucide-react';

/**
 * Компонент секции параметров помещения
 */
const RoomParametersSection = ({ 
  formData, 
  handleInputChange, 
  handleWallsChange,
  errors, 
  areaTypes 
}) => {
  // Обработчик изменения выбираемых стен
  const toggleWall = (wall) => {
    const currentWalls = [...(formData.wallsToTile || [])];
    
    // Если выбрана опция "все стены", обработаем специально
    if (wall === 'all') {
      if (currentWalls.includes('all')) {
        // Если "все стены" уже выбраны, то снимаем все выборы
        handleWallsChange([]);
      } else {
        // Иначе выбираем только "все стены"
        handleWallsChange(['all']);
      }
      return;
    }
    
    // Если выбрана конкретная стена
    if (wall === 'floor') {
      if (currentWalls.includes('floor')) {
        // Если пол уже выбран, убираем его
        handleWallsChange(currentWalls.filter(w => w !== 'floor'));
      } else {
        // Добавляем пол и убираем "все стены", если она выбрана
        const newWalls = currentWalls.filter(w => w !== 'all');
        newWalls.push('floor');
        handleWallsChange(newWalls);
      }
      return;
    }
    
    // Обработка для конкретных стен (north, south, east, west)
    if (currentWalls.includes(wall)) {
      // Если стена уже выбрана, убираем ее
      handleWallsChange(currentWalls.filter(w => w !== wall));
    } else {
      // Добавляем эту стену и убираем "все стены", если она выбрана
      const newWalls = currentWalls.filter(w => w !== 'all');
      newWalls.push(wall);
      handleWallsChange(newWalls);
    }
  };

  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <LayoutGrid className="w-5 h-5 mr-2 text-fuchsia-500" />
        Параметры помещения
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Тип помещения */}
        <div>
          <label htmlFor="areaType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип помещения
          </label>
          <select
            id="areaType"
            name="areaType"
            value={formData.areaType}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {areaTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* Форма помещения */}
        <div>
          <label htmlFor="roomShape" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Форма помещения
          </label>
          <select
            id="roomShape"
            name="roomShape"
            value={formData.roomShape}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="rectangle">Прямоугольная</option>
            <option value="l-shaped">L-образная</option>
            <option value="complex">Сложная</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Ширина помещения */}
        <div>
          <label htmlFor="roomWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Ruler className="w-4 h-4 mr-2 text-fuchsia-500" />
            Ширина (м)
          </label>
          <input
            type="text"
            id="roomWidth"
            name="roomWidth"
            value={formData.roomWidth}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.roomWidth ? 'input-error' : ''}`}
            placeholder="Ширина комнаты"
          />
          {errors.roomWidth && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.roomWidth}
            </p>
          )}
        </div>

        {/* Длина помещения */}
        <div>
          <label htmlFor="roomLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Ruler className="w-4 h-4 mr-2 text-fuchsia-500" />
            Длина (м)
          </label>
          <input
            type="text"
            id="roomLength"
            name="roomLength"
            value={formData.roomLength}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.roomLength ? 'input-error' : ''}`}
            placeholder="Длина комнаты"
          />
          {errors.roomLength && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.roomLength}
            </p>
          )}
        </div>

        {/* Высота помещения */}
        <div>
          <label htmlFor="roomHeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Ruler className="w-4 h-4 mr-2 text-fuchsia-500" />
            Высота (м)
          </label>
          <input
            type="text"
            id="roomHeight"
            name="roomHeight"
            value={formData.roomHeight}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.roomHeight ? 'input-error' : ''}`}
            placeholder="Высота комнаты"
          />
          {errors.roomHeight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.roomHeight}
            </p>
          )}
        </div>
      </div>

      {/* Дополнительная площадь для L-образных и сложных комнат */}
      {(formData.roomShape === 'l-shaped' || formData.roomShape === 'complex') && (
        <div className="mb-4">
          <label htmlFor="additionalArea" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Дополнительная площадь (м²)
          </label>
          <input
            type="text"
            id="additionalArea"
            name="additionalArea"
            value={formData.additionalArea}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.additionalArea ? 'input-error' : ''}`}
            placeholder="Дополнительная площадь"
          />
          {errors.additionalArea && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.additionalArea}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {formData.roomShape === 'l-shaped' 
              ? 'Укажите площадь выступающей части L-образной комнаты, которая не вошла в произведение ширины на длину'
              : 'Укажите дополнительную площадь для сложной формы комнаты'}
          </p>
        </div>
      )}

      {/* Выбор стен для облицовки плиткой */}
      <div className="mb-2">
        <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <HomeIcon className="w-4 h-4 mr-2 text-fuchsia-500" />
          Какие поверхности облицовываем плиткой?
        </p>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div 
            className={`border rounded-lg p-3 cursor-pointer transition-colors 
              ${formData.wallsToTile.includes('all') 
                ? 'bg-fuchsia-100 border-fuchsia-300 dark:bg-fuchsia-900/30 dark:border-fuchsia-700' 
                : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            onClick={() => toggleWall('all')}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.wallsToTile.includes('all')}
                onChange={() => {}} // Обработчик присоединен к родительскому div
                className="form-checkbox"
                readOnly
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Все стены
              </span>
            </div>
          </div>
          
          <div 
            className={`border rounded-lg p-3 cursor-pointer transition-colors 
              ${formData.wallsToTile.includes('floor') 
                ? 'bg-fuchsia-100 border-fuchsia-300 dark:bg-fuchsia-900/30 dark:border-fuchsia-700' 
                : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            onClick={() => toggleWall('floor')}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.wallsToTile.includes('floor')}
                onChange={() => {}}
                className="form-checkbox"
                readOnly
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Пол
              </span>
            </div>
          </div>
          
          <div 
            className={`border rounded-lg p-3 cursor-pointer transition-colors 
              ${formData.wallsToTile.includes('north') 
                ? 'bg-fuchsia-100 border-fuchsia-300 dark:bg-fuchsia-900/30 dark:border-fuchsia-700' 
                : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            onClick={() => toggleWall('north')}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.wallsToTile.includes('north')}
                onChange={() => {}}
                className="form-checkbox"
                readOnly
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Стена (север)
              </span>
            </div>
          </div>
          
          <div 
            className={`border rounded-lg p-3 cursor-pointer transition-colors 
              ${formData.wallsToTile.includes('south') 
                ? 'bg-fuchsia-100 border-fuchsia-300 dark:bg-fuchsia-900/30 dark:border-fuchsia-700' 
                : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            onClick={() => toggleWall('south')}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.wallsToTile.includes('south')}
                onChange={() => {}}
                className="form-checkbox"
                readOnly
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Стена (юг)
              </span>
            </div>
          </div>
          
          <div 
            className={`border rounded-lg p-3 cursor-pointer transition-colors 
              ${formData.wallsToTile.includes('east') 
                ? 'bg-fuchsia-100 border-fuchsia-300 dark:bg-fuchsia-900/30 dark:border-fuchsia-700' 
                : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            onClick={() => toggleWall('east')}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.wallsToTile.includes('east')}
                onChange={() => {}}
                className="form-checkbox"
                readOnly
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Стена (восток)
              </span>
            </div>
          </div>
          
          <div 
            className={`border rounded-lg p-3 cursor-pointer transition-colors 
              ${formData.wallsToTile.includes('west') 
                ? 'bg-fuchsia-100 border-fuchsia-300 dark:bg-fuchsia-900/30 dark:border-fuchsia-700' 
                : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            onClick={() => toggleWall('west')}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.wallsToTile.includes('west')}
                onChange={() => {}}
                className="form-checkbox"
                readOnly
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Стена (запад)
              </span>
            </div>
          </div>
        </div>
        
        {formData.wallsToTile.length === 0 && (
          <p className="mt-2 text-xs text-orange-500 dark:text-orange-400">
            Выберите хотя бы одну поверхность для облицовки плиткой. По умолчанию расчет будет выполнен для пола.
          </p>
        )}
      </div>
      
      <div className="mt-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/10 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 text-xs text-fuchsia-700 dark:text-fuchsia-300">
        <div className="flex items-start">
          <DoorOpen className="w-4 h-4 text-fuchsia-600 dark:text-fuchsia-400 mt-0.5 mr-2 flex-shrink-0" />
          <div>
            При выборе "Все стены" расчет производится для всех стен комнаты (периметр × высота). 
            При выборе отдельных стен вы можете исключить, например, стены с окнами или дверьми.
            Выбрав опцию "Пол", добавьте расчет для пола помещения.
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomParametersSection;